
export interface DiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

/**
 * A simple word-level diff algorithm based on Longest Common Subsequence (LCS).
 * This allows us to show editors exactly what the AI changed.
 */
export function diffWords(oldText: string, newText: string): DiffPart[] {
  // 1. Tokenize by splitting on whitespace, but keep the whitespace attached or separate to preserve formatting
  // For simplicity in this UI, we split by space.
  const oldWords = oldText.split(/\s+/);
  const newWords = newText.split(/\s+/);
  
  // Handle empty cases
  if (!oldText) return newWords.map(w => ({ value: w, added: true }));
  if (!newText) return oldWords.map(w => ({ value: w, removed: true }));

  const m = oldWords.length;
  const n = newWords.length;
  
  // 2. Compute LCS Matrix
  const dp: number[][] = Array(m + 1).fill(0).map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 3. Backtrack to find diff
  let i = m;
  let j = n;
  const diffs: DiffPart[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      diffs.unshift({ value: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      diffs.unshift({ value: newWords[j - 1], added: true });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      diffs.unshift({ value: oldWords[i - 1], removed: true });
      i--;
    }
  }

  return diffs;
}

/**
 * Calculates simple statistics about the changes
 */
export function getDiffStats(diffs: DiffPart[]) {
  const added = diffs.filter(d => d.added).length;
  const removed = diffs.filter(d => d.removed).length;
  return { added, removed };
}
